import { useCallback, useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { Paperclip, WarningCircle } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";

interface ChecklistRecipient {
    name: string;
    email: string;
}

interface ChecklistPreview {
    subject: string;
    html: string;
    recipients: ChecklistRecipient[];
    attachmentNames: string[];
    missingLanguages: string[];
}

interface OutgoingChecklistPreviewModalProps {
    isOpen: boolean;
    contractId: number | null;
    unitId?: string;
    onClose: () => void;
    onConfirm: (contractId: number) => void;
}

/**
 * Voorbeeldweergave van de mail met de checklijst gebreken voor de uitgaande
 * plaatsbeschrijving.
 *
 * De inhoud komt van de backend (`GET /rental/contracts/:id/checklist-preview`)
 * en niet uit dit component: onderwerp, tekst en bijlagen worden daar op exact
 * dezelfde manier opgebouwd als bij het effectief verzenden. Eerder stond de
 * tekst hier hardgecodeerd, waardoor het voorbeeld iets anders toonde dan wat
 * de huurder kreeg.
 */
const OutgoingChecklistPreviewModal = ({
    isOpen,
    contractId,
    unitId,
    onClose,
    onConfirm,
}: OutgoingChecklistPreviewModalProps) => {
    const { session } = useSession();
    const [preview, setPreview] = useState<ChecklistPreview | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchPreview = useCallback(async () => {
        if (!contractId) return;

        try {
            setIsLoading(true);
            setError(null);

            const response = await fetchWithAuth(
                `${API_URL}/rental/contracts/${contractId}/checklist-preview`,
                { method: "GET" },
                session
            );

            if (!response.ok) {
                const body = await response.json().catch(() => null);
                throw new Error(
                    body?.message ||
                    `Kon het voorbeeld niet ophalen (${response.status})`
                );
            }

            setPreview(await response.json());
        } catch (err) {
            console.error("Failed to fetch checklist preview:", err);
            setError(
                err instanceof Error
                    ? err.message
                    : "Kon het voorbeeld van de checklijstmail niet ophalen."
            );
            setPreview(null);
        } finally {
            setIsLoading(false);
        }
    }, [contractId, session]);

    useEffect(() => {
        if (isOpen && contractId) {
            fetchPreview();
        } else {
            setPreview(null);
            setError(null);
        }
    }, [isOpen, contractId, fetchPreview]);

    const canSend = Boolean(
        preview && preview.recipients.length > 0 && preview.attachmentNames.length > 0
    );

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden">
                <DialogHeader>
                    <DialogTitle>Checklijst versturen</DialogTitle>
                    <DialogDescription>
                        {unitId
                            ? `Voorbeeld van de mail voor unit ${unitId}, zoals de huurder ze krijgt.`
                            : "Voorbeeld van de mail zoals de huurder ze krijgt."}
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
                    {isLoading && (
                        <div className="flex h-40 items-center justify-center">
                            <Spinner className="size-6" />
                        </div>
                    )}

                    {!isLoading && error && (
                        <Alert variant="destructive">
                            <WarningCircle className="size-4" />
                            <AlertTitle>Voorbeeld niet beschikbaar</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {!isLoading && preview && (
                        <>
                            {preview.missingLanguages.length > 0 && (
                                <Alert>
                                    <WarningCircle className="size-4" />
                                    <AlertTitle>
                                        Nog geen {preview.missingLanguages.join(" en ")}-versie
                                    </AlertTitle>
                                    <AlertDescription>
                                        De mail is tweetalig, maar de checklijst gaat enkel mee in
                                        de talen die bij Documenten op YallyHub staan. Laad
                                        {" "}
                                        {preview.missingLanguages
                                            .map((language) => `Checklijst_gebreken_Uitgaande_${language}.xlsx`)
                                            .join(" en ")}{" "}
                                        daar op en ze gaat automatisch mee.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <section>
                                <h3 className="mb-1 text-sm font-medium">Ontvangers</h3>
                                {preview.recipients.length ? (
                                    <div className="flex flex-wrap gap-2">
                                        {preview.recipients.map((recipient) => (
                                            <Badge key={recipient.email} variant="secondary">
                                                {recipient.name} ({recipient.email})
                                            </Badge>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-destructive">
                                        Geen e-mailadres gevonden bij de huurders van dit contract.
                                    </p>
                                )}
                            </section>

                            <section>
                                <h3 className="mb-1 text-sm font-medium">Onderwerp</h3>
                                <p className="text-sm text-muted-foreground">{preview.subject}</p>
                            </section>

                            <section>
                                <h3 className="mb-1 text-sm font-medium">Bijlagen</h3>
                                {preview.attachmentNames.length ? (
                                    <ul className="space-y-1">
                                        {preview.attachmentNames.map((name) => (
                                            <li
                                                key={name}
                                                className="flex items-center gap-2 text-sm text-muted-foreground"
                                            >
                                                <Paperclip className="size-4 shrink-0" />
                                                {name}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-destructive">
                                        Geen checklijst gevonden bij Documenten op YallyHub.
                                    </p>
                                )}
                            </section>

                            <section>
                                <h3 className="mb-1 text-sm font-medium">Inhoud</h3>
                                <div
                                    className="overflow-x-auto rounded-lg border bg-muted/40"
                                    // De mail is opgemaakte HTML uit onze eigen template; ze wordt
                                    // hier getoond zoals ze in het postvak van de huurder aankomt.
                                    dangerouslySetInnerHTML={{ __html: preview.html }}
                                />
                            </section>
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Annuleren
                    </Button>
                    <Button
                        disabled={!canSend}
                        onClick={() => {
                            if (contractId) onConfirm(contractId);
                        }}
                    >
                        Verzenden
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default OutgoingChecklistPreviewModal;
